import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { LiveSearchMetadataWebComponentSchema } from '../types';
import { QueryExecution, QueryExecutionEndpointStatistics } from '../../types';
import { isQueryExecutionFinished } from '../../utils';
import { LiveSearchMetadataGridComponent } from '../../metadataGrid';
import { GzDialogWebComponent } from '../../../../ui/gzDialog';
import { GzDialog } from '../../../../ui/gzDialog/component/GzDialog';
import { styles as modalStyles } from './template-modal.scss';
import { EXPORT_CSV_RESULT_METADATA_EVENT } from '../../metadataGrid/types/index';

export class LiveSearchMetadataView extends BaseWebComponentView {
  private lastQueryExecutionId: string | null = null;
  public pageTitle: string;

  public dom: {
    $wrapper: HTMLElement;
    $metadataHeader: HTMLDivElement;
    $gzMetadataTitle: HTMLSpanElement;
    $statusText: HTMLSpanElement;
    $statusValue: HTMLSpanElement;
    $respondentsText: HTMLSpanElement;
    $respondentsValue: HTMLSpanElement;
    $totalEndpointsText: HTMLSpanElement;
    $totalEndpointsValue: HTMLSpanElement;
    $metadataViewMoreButton: HTMLButtonElement;
  };

  getDomElements(): void {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$metadataHeader = this.shadow.querySelector('#gz-metadata-header');
    this.dom.$gzMetadataTitle = this.shadow.querySelector('#title-text');
    this.dom.$statusText = this.shadow.querySelector('#status-text');
    this.dom.$statusValue = this.shadow.querySelector('#status-value');
    this.dom.$respondentsText = this.shadow.querySelector('#respondents-text');
    this.dom.$respondentsValue = this.shadow.querySelector('#respondents-value');
    this.dom.$totalEndpointsText = this.shadow.querySelector('#total-endpoints-text');
    this.dom.$totalEndpointsValue = this.shadow.querySelector('#total-endpoints-value');
    this.dom.$metadataViewMoreButton = this.shadow.querySelector('.gz-metadata-view-more-button');
    this.dom.$gzMetadataTitle.innerText = this.dependencies.translateFn('Metadata details');
    this.dom.$statusText.innerText = this.dependencies.translateFn('Status:');
    this.dom.$respondentsText.innerText = this.dependencies.translateFn('Respondents:');
    this.dom.$totalEndpointsText.innerText = this.dependencies.translateFn('Total endpoints:');
    this.dom.$metadataViewMoreButton.innerText = this.dependencies.translateFn('View more');
  }

  public buildView(data: LiveSearchMetadataWebComponentSchema): void {
    if (data.queryExecution && this.lastQueryExecutionId !== data.queryExecution._id) {
      this.lastQueryExecutionId = data.queryExecution._id;
    }

    if (
      !data.queryExecution ||
      data.queryExecution.hasExpiredResultsMetadata ||
      !data.queryExecution.endpointsStatistics
    ) {
      this.setInitialDisplay();
      return;
    }

    if (isQueryExecutionFinished(data.queryExecution)) {
      this.dom.$statusValue.innerText = this.dependencies.translateFn('Finalized');
      this.dom.$metadataViewMoreButton.disabled = false;
      this.dom.$metadataHeader.className = 'gz-metadata-header-enabled';
      this.displayHeaderTexts(data.queryExecution.endpointsStatistics);
      return;
    }

    this.dom.$statusValue.innerText = this.dependencies.translateFn('In progress');
    this.disableComponent();
    this.displayHeaderTexts(data.queryExecution.endpointsStatistics);
  }

  private disableComponent(): void {
    this.dom.$metadataViewMoreButton.disabled = true;
    this.dom.$metadataHeader.className = 'gz-metadata-header-disabled';
  }

  private setInitialDisplay(): void {
    this.setMetadataHeaderToNA();
    this.disableComponent();
  }

  private displayHeaderTexts(endpointsStatistics: QueryExecutionEndpointStatistics): void {
    this.dom.$respondentsValue.innerText = (
      endpointsStatistics.withDeliveredResultsCount + endpointsStatistics.withDeliveryErrorCount
    ).toString();
    this.dom.$totalEndpointsValue.innerText = endpointsStatistics.totalCount.toString();
  }

  private setMetadataHeaderToNA(): void {
    this.dom.$statusValue.innerText = this.dependencies.translateFn('N/A');
    this.dom.$respondentsValue.innerText = this.dependencies.translateFn('N/A');
    this.dom.$totalEndpointsValue.innerText = this.dependencies.translateFn('N/A');
  }

  public showMetadataPopup(queryExecution: QueryExecution | null): void {
    const gridMetadataComponent = new LiveSearchMetadataGridComponent();
    gridMetadataComponent.addEventListener(EXPORT_CSV_RESULT_METADATA_EVENT, (e) => {
      this.dom.$wrapper.dispatchEvent(new CustomEvent(EXPORT_CSV_RESULT_METADATA_EVENT, {
        bubbles: true,
        composed: true,
      }));
    });
    const bodyContent = document.createElement('div');
    bodyContent.style.height = '100%';
    bodyContent.innerHTML = `
        <style>${modalStyles}</style>
        <div class="gz-metadata-details">
            <div>
              <span>${this.dom.$statusText.innerText}</span>
              <span class="metadata-header-values">${this.dom.$statusValue.innerText}</span>
            </div>
            <div>
              <span>${this.dom.$respondentsText.innerText}</span>
              <span class="metadata-header-values">${this.dom.$respondentsValue.innerText}</span>
            </div>
            <div>
              <span>${this.dom.$totalEndpointsText.innerText}</span>
              <span class="metadata-header-values">${this.dom.$totalEndpointsValue.innerText}</span>
            </div>
        </div>
    `;

    bodyContent.appendChild(gridMetadataComponent);
    this.dom.$wrapper.appendChild(bodyContent);

    gridMetadataComponent.injectDependencies(this.dependencies);
    gridMetadataComponent.start();
    gridMetadataComponent.setQueryExecution(queryExecution);

    const footerContent = document.createElement('div');
    footerContent.innerHTML = `
        <div></div>
        <gz-button id="button-cancel" type="outline" color="secondary">
          ${this.dependencies.translateFn('CLOSE')}
        </gz-button>
    `;

    footerContent.querySelector('#button-cancel')?.addEventListener('click', () => {
      dialog.dismiss();
    });

    const windowConfigs: GzDialog = {
      headline: this.dependencies.translateFn('Metadata details - {pageTitle}', { pageTitle: this.pageTitle }),
      bodyContent: bodyContent,
      footerContent: footerContent,
      isOpen: true,
      closableOutsideBox: false,
      isWindow: true,
      overlayZIndex: 999,
    };

    const dialog = GzDialogWebComponent.buildDialogElement(
      windowConfigs,
      document.querySelector('.mainContainer') as HTMLElement
    );
  }
}
